import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { useTranslation, withTranslation } from 'react-i18next';
import { AlertNotice, Spinner } from 'shared/components';
import * as notificationsActions from 'features/notifications/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import isEmpty from 'lodash/isEmpty';
import { NOTIFICATION_STATUS } from 'shared/constants';
import { mapAllNotificationsDays } from 'features/notifications/selectors';
import InfiniteScroll from 'react-infinite-scroll-component';
import { NotificationDetails } from 'features/notifications';

import './NotificationsContainer.scss';

const NOTIFICATIONS_LIMIT = 12;

class NotificationsContainer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      notification: null,
      hasMoreItems: true,
    };

    this.openNotification = this.openNotification.bind(this);
    this.closeNotification = this.closeNotification.bind(this);
    this.loadItems = this.loadItems.bind(this);
  }

  componentDidMount() {
    const { notifications, user } = this.props;

    this.props.actions.clearNotificationsAction();
    this.props.actions.getAllNotifications(user.id, {
      limit: NOTIFICATIONS_LIMIT,
    });
    this.props.actions.getNotViewedNotificationsCount(user.id);
    this.props.actions.getViewedNotificationsCount(user.id);
    this.props.actions.getServerDateTime();
  }

  componentDidUpdate(prevProps) {
    const { loadingNotifications, notifications, noMoreNotifications } = this.props;
    if (
      notifications &&
      notifications.length &&
      (notifications.length % NOTIFICATIONS_LIMIT !== 0 || noMoreNotifications) &&
      !loadingNotifications &&
      (prevProps.notifications !== notifications ||
        prevProps.loadingNotifications !== loadingNotifications)
    ) {
      this.setState({
        hasMoreItems: false,
      });
    }
  }

  openNotification(notification) {
    const { user } = this.props;

    this.setState({
      notification: notification,
    });

    if (notification.status === NOTIFICATION_STATUS.notViewed) {
      this.props.actions.setNotificationAsRead(user.id, {
        notificationId: notification.id,
      });
    }
  }

  closeNotification() {
    const { user } = this.props;

    const { notification } = this.state;

    this.setState({
      notification: null,
    });

    if (notification.status === NOTIFICATION_STATUS.notViewed) {
      this.props.actions.clearNotificationsAction();
      this.props.actions.getAllNotifications(user.id, {
        limit: NOTIFICATIONS_LIMIT,
      });
      this.props.actions.getNotViewedNotificationsCount(user.id);
      this.props.actions.getViewedNotificationsCount(user.id);
    }
  }

  loadItems() {
    const { notifications, user } = this.props;

    this.props.actions
      .getAllNotifications(user.id, {
        limit: NOTIFICATIONS_LIMIT,
        skip: notifications.length,
      })
      .then(() => {
        if (
          this.props.notifications.length % NOTIFICATIONS_LIMIT !== 0 ||
          this.props.noMoreNotifications
        ) {
          this.setState({
            hasMoreItems: false,
          });
        }
      });
  }

  render() {
    const { notifications, notViewedCount, viewedCount, loadingNotifications, errorNotifications } =
      this.props;

    const { notification, hasMoreItems } = this.state;

    const notificationsCount = notifications
      ? notViewedCount + viewedCount > 100
        ? '+99'
        : notViewedCount + viewedCount
      : 0;
    const loader = <Spinner />;

    if (loadingNotifications && !notifications.length) {
      return <Spinner />;
    }

    return (
      <div className="notifications-container">
        <h4>{this.props.t('notifications.title', { count: notificationsCount })}</h4>

        {isEmpty(errorNotifications) ? (
          notifications.length ? (
            <div>
              <InfiniteScroll
                dataLength={notifications.length}
                next={this.loadItems.bind(this)}
                hasMore={hasMoreItems}
                loader={loader}>
                <div>
                  {notifications.map((notification) => (
                    <NotificationRow
                      notification={notification}
                      key={notification.id}
                      openNotification={this.openNotification}
                    />
                  ))}
                </div>
              </InfiniteScroll>
              <NotificationDetails
                modalIsOpen={!isEmpty(notification)}
                notification={notification}
                closeNotification={this.closeNotification}
              />
            </div>
          ) : (
            <AlertNotice message={this.props.t('notifications.noNotifications')} inline={true} />
          )
        ) : (
          <AlertNotice message={this.props.t('errors.serverError')} inline={true} type="error" />
        )}
      </div>
    );
  }
}

const NotificationRow = (props) => {
  const { t } = useTranslation();
  return (
    <div
      className={
        props.notification.status === NOTIFICATION_STATUS.notViewed
          ? 'notification unread'
          : 'notification'
      }>
      <div className="circle">
        <i className="fa fa-circle" aria-hidden="true" />
      </div>
      <div className="info">
        <div>{props.notification.title}</div>
        <div>
          {props.notification.days
            ? t('notifications.daysLabel', {
                count: props.notification.days,
              })
            : t('common.labels.today')}
          {props.notification.status === NOTIFICATION_STATUS.notViewed && (
            <span> - {t('common.labels.unread')}</span>
          )}
        </div>
      </div>
      <div
        className="open-notification btn-link"
        onClick={() => props.openNotification(props.notification)}>
        <span>{t('common.labels.open')}</span>
        <i className="fa fa-angle-right" />
      </div>
    </div>
  );
};

NotificationsContainer.propTypes = {
  actions: PropTypes.shape({
    getAllNotifications: PropTypes.func.isRequired,
    getNotViewedNotificationsCount: PropTypes.func.isRequired,
    getViewedNotificationsCount: PropTypes.func.isRequired,
    clearNotificationsAction: PropTypes.func,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
};

function mapStateToProps(state) {
  return {
    user: state.users.currentUser,
    notifications: mapAllNotificationsDays(state),
    loadingNotifications: state.notifications.loadingNotifications,
    noMoreNotifications: state.notifications.noMoreNotifications,
    notViewedCount: state.notifications.notViewedNotificationsCount,
    viewedCount: state.notifications.viewedNotificationsCount,
    errorNotifications: state.notifications.errorNotifications,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...notificationsActions, ...staticActions }, dispatch),
  };
}

export default withTranslation()(
  connect(mapStateToProps, mapDispatchToProps)(NotificationsContainer)
);
